import { SubscriptionNoticeBannerVariant } from '@/account/components/subscription-notice-banner/SubscriptionNoticeBanner'
import { StripeSubscriptionInterval } from '@/account/store/schemas'
import { TextSizeLabel } from '@/artifact-editor/components/TextSizeDropdown'
// Domain-specific enums
import { RiaToggleState, RiaDialogSource, RiaToggleName, RiaTab, RiaSourceTriggerLocation, RiaViewerTab, RiaCitatorSection } from '@/analytics/enums/ria.enums'
import { CitatorBadgeType, CitatorSurface, CitatorPanelSource, CitatorTreatmentOutcome } from '@/analytics/enums/citator.enums'
import { ArtifactTemplateSource, ArtifactExportType, ArtifactTemplateApplyErrorType } from '@/analytics/enums/artifact-template.enums'
import { DraftEntryPoint } from '@/analytics/enums/draft.enums'
import { MatterJurisdictionEntrySource } from '@/analytics/enums/matter-jurisdiction.enums'

export enum SkillSource {
  modal = 'modal',
  quick_action = 'quick_action',
}

// Template Source (where the template was added from)
export enum TemplateSource {
  upload = 'upload',
  drive = 'drive',
}

export enum TemplateUploadErrorType {
  processing = 'processing_error',
  validation = 'validation_error',
}

export enum InlineEditResolvedAction {
  Replaced = 'replaced',
  InsertedBelow = 'inserted_below',
  Declined = 'declined',
  NoDiff = 'no_diff',
}

export enum AnalyticsEvent {
  SignUp = 'SignUp',
  PaymentMethodAdded = 'PaymentMethodAdded',
  PaymentMethodAddedMonthly = 'PaymentMethodAddedMonthly',
  PaymentMethodAddedAnnual = 'PaymentMethodAddedAnnual',
  NewChat = 'NewChat',
  NewChatMessage = 'NewChatMessage',
  OpenExistingConversation = 'OpenExistingConversation',
  OpenConversationSubmenu = 'OpenConversationSubmenu',
  OpenConversationDeletionDialog = 'OpenConversationDeletionDialog',
  ConversationDeleted = 'ConversationDeleted',
  DownloadConversationToDocx = 'DownloadConversationToDocx',
  OpenConversationRetitleDialog = 'OpenConversationRetitleDialog',
  ConversationRetitled = 'ConversationRetitled',
  CopyChatResponse = 'CopyChatResponse',
  DownloadChatResponseToDocx = 'DownloadChatResponseToDocx',
  OpenChatDialog = 'OpenChatDialog',
  FileAnalysisFileUpload = 'FileAnalysisFileUpload',
  AssistantFilePillPreviewOpened = 'AssistantFilePillPreviewOpened',
  BooleanComposerSubmission = 'BooleanComposerSubmission',
  CompareDocumentsSubmission = 'CompareDocumentsSubmission',
  SharePaxtonClicked = 'SharePaxtonClicked',
  DownloadChatSourceToDocx = 'DownloadChatSourceToDocx',
  ReferralDialogOpened = 'ReferralDialogOpened',
  ReferralLinkCopied = 'ReferralLinkCopied',
  ReferralLinkCopiedFromInput = 'ReferralLinkCopiedFromInput',
  ContactUsForEnterpriseClicked = 'ContactUsForEnterpriseClicked',
  MatterCreated = 'MatterCreated',
  MatterUpdated = 'MatterUpdated',
  MatterDeleted = 'MatterDeleted',
  MatterInviteSent = 'MatterInviteSent',
  MatterInviteSendFailed = 'MatterInviteSendFailed',
  MatterInviteAccepted = 'MatterInviteAccepted',
  MatterInviteAcceptFailed = 'MatterInviteAcceptFailed',
  MatterInviteRejected = 'MatterInviteRejected',
  MatterInviteRejectFailed = 'MatterInviteRejectFailed',
  MatterSelectionChanged = 'MatterSelectionChanged',
  OnboardingFormSubmitted = 'OnboardingFormSubmitted',
  SkillsModalOpened = 'SkillsModalOpened',
  SkillQuickActionClick = 'SkillQuickActionClick',
  SkillsModalSelected = 'SkillsModalSelected',
  SkillPromptSubmitted = 'SkillPromptSubmitted',
  ArtifactEditorOpened = 'ArtifactEditorOpened',
  ArtifactEditorFormattingUsed = 'ArtifactEditorFormattingUsed',
  ArtifactEditorAutoSaveSuccess = 'ArtifactEditorAutoSaveSuccess',
  ArtifactEditorSessionCompleted = 'ArtifactEditorSessionCompleted',
  ArtifactEditorFirstEdit = 'ArtifactEditorFirstEdit',
  ArtifactEditorTextSizeChanged = 'ArtifactEditorTextSizeChanged',
  ArtifactEditorSaveError = 'ArtifactEditorSaveError',
  InlineEditSubmitted = 'InlineEditSubmitted',
  InlineEditCompleted = 'InlineEditCompleted',
  InlineEditResolved = 'InlineEditResolved',
  SubscriptionNoticeBannerDismissed = 'SubscriptionNoticeBannerDismissed',
  SubscriptionNoticeBannerCTAClicked = 'SubscriptionNoticeBannerCTAClicked',
  StripePortalOpened = 'StripePortalOpened',
  AppUpdateRefreshClicked = 'AppUpdateRefreshClicked',
  ArtifactDownloadSuccess = 'ArtifactDownloadSuccess',
  ArtifactDownloadFailed = 'ArtifactDownloadFailed',
  ClipboardCopy = 'ClipboardCopy',
  ApplyTemplateTabOpened = 'ApplyTemplateTabOpened',
  ApplyTemplateTabClosed = 'ApplyTemplateTabClosed',
  TemplateSourceChosen = 'TemplateSourceChosen',
  TemplateDriveDialogOpened = 'TemplateDriveDialogOpened',
  TemplateSourceDialogDismissed = 'TemplateSourceDialogDismissed',
  TemplateUploadStarted = 'TemplateUploadStarted',
  TemplateUploadCompleted = 'TemplateUploadCompleted',
  TemplateUploadFailed = 'TemplateUploadFailed',
  TemplateResponseSucceeded = 'TemplateResponseSucceeded',
  TemplateResponseFailed = 'TemplateResponseFailed',
  TemplateResponseCanceled = 'TemplateResponseCanceled',
  TemplateInfluencedFeedback = 'TemplateInfluencedFeedback',
  WritingSampleNotifyMeClicked = 'WritingSampleNotifyMeClicked',
  ClipboardPaste = 'ClipboardPaste',
  ResponseThumbUp = 'ResponseThumbUp',
  ResponseThumbDown = 'ResponseThumbDown',
  ArtifactClipboardCopy = 'ArtifactClipboardCopy',
  OrganizationCreated = 'OrganizationCreated',
  OrganizationInviteAccepted = 'OrganizationInviteAccepted',
  OrganizationInviteSent = 'OrganizationInviteSent',
  OrganizationUserRemoved = 'OrganizationUserRemoved',
  ConversationMatterChanged = 'ConversationMatterChanged',
  MedSummaryFeatureOpened = 'MedSummaryFeatureOpened',
  MedSummaryTabChanged = 'MedSummaryTabChanged',
  MedSummaryStarted = 'MedSummaryStarted',
  MedSummaryNameEntered = 'MedSummaryNameEntered',
  MedSummaryFileUploadStarted = 'MedSummaryFileUploadStarted',
  MedSummaryFileUploadCompleted = 'MedSummaryFileUploadCompleted',
  MedSummaryFileUploadFailed = 'MedSummaryFileUploadFailed',
  MedSummaryDriveDialogOpened = 'MedSummaryDriveDialogOpened',
  MedSummaryMatterFilesDialogOpened = 'MedSummaryMatterFilesDialogOpened',
  MedSummaryDriveFileSelected = 'MedSummaryDriveFileSelected',
  MedSummaryFileRemoved = 'MedSummaryFileRemoved',
  MedSummaryGenerateClicked = 'MedSummaryGenerateClicked',
  MedSummaryJobCreated = 'MedSummaryJobCreated',
  MedSummaryListViewed = 'MedSummaryListViewed',
  MedSummaryJobViewerOpened = 'MedSummaryJobViewerOpened',
  MedSummaryDownloadStarted = 'MedSummaryDownloadStarted',
  MedSummaryDownloadCompleted = 'MedSummaryDownloadCompleted',
  MedSummaryDownloadFailed = 'MedSummaryDownloadFailed',
  // Medical Billing Summary – Living asset (single matter billing summary)
  MedSummaryAssetGenerateClicked = 'MedSummaryAssetGenerateClicked',
  MedSummaryAssetUpdateClicked = 'MedSummaryAssetUpdateClicked',
  MedSummaryAssetRetryFailedClicked = 'MedSummaryAssetRetryFailedClicked',
  MedSummaryAssetCompleteExcludingFailedClicked = 'MedSummaryAssetCompleteExcludingFailedClicked',
  MedSummaryAssetFileRemoved = 'MedSummaryAssetFileRemoved',
  MedSummaryAssetDownloadClicked = 'MedSummaryAssetDownloadClicked',
  MedSummaryAssetReferenceViewerOpened = 'MedSummaryAssetReferenceViewerOpened',
  // Medical Chronology – Legacy Reports (download only)
  SideBarBillingSummariesClicked = 'SideBarBillingSummariesClicked',
  MedChronDownloadClicked = 'MedChronDownloadClicked',
  // Medical Chronology – Living asset (single matter chronology)
  MedChronAssetChatClicked = 'MedChronAssetChatClicked',
  MedChronAssetDownloadClicked = 'MedChronAssetDownloadClicked',
  MedChronAssetGenerateClicked = 'MedChronAssetGenerateClicked',
  MedChronAssetUpdateClicked = 'MedChronAssetUpdateClicked',
  MedChronAssetFileRemoved = 'MedChronAssetFileRemoved',
  MedChronAssetReferenceViewerOpened = 'MedChronAssetReferenceViewerOpened',
  // Timeline (Single Matter) – Living asset (single matter timeline)
  TimelineSourceOpenFailed = 'TimelineSourceOpenFailed',
  TimelineAssetReferenceViewerOpened = 'TimelineAssetReferenceViewerOpened',
  TimelineAssetDownloadClicked = 'TimelineAssetDownloadClicked',
  TimelineAssetGenerateClicked = 'TimelineAssetGenerateClicked',
  TimelineAssetUpdateClicked = 'TimelineAssetUpdateClicked',
  TimelineAssetFileRemoved = 'TimelineAssetFileRemoved',
  TimelineAssetChatClicked = 'TimelineAssetChatClicked',
  // Research in Assistant – Discovery & Intent
  RiaToggleClicked = 'RiaToggleClicked',
  RiaSourcesButtonClicked = 'RiaSourcesButtonClicked',
  RiaSourcesDialogOpened = 'RiaSourcesDialogOpened',
  // Research in Assistant – Configuration & Submission
  RiaSourcesTabChanged = 'RiaSourcesTabChanged',
  RiaSourcesClearAllClicked = 'RiaSourcesClearAllClicked',
  RiaSourcesApplyClicked = 'RiaSourcesApplyClicked',
  RiaSelectAllFederalChecked = 'RiaSelectAllFederalChecked',
  RiaSelectAllFederalUnchecked = 'RiaSelectAllFederalUnchecked',
  // Research in Assistant – Usage & Engagement
  RiaMessageWithResearchSubmitted = 'RiaMessageWithResearchSubmitted',
  RiaResponseReceived = 'RiaResponseReceived',
  RiaSourceViewerOpened = 'RiaSourceViewerOpened',
  RiaSourceDownloadClicked = 'RiaSourceDownloadClicked',
  RiaSourceViewerTabChanged = 'RiaSourceViewerTabChanged',
  RiaCaseAnalysisRequested = 'RiaCaseAnalysisRequested',
  RiaCitatorSectionExpanded = 'RiaCitatorSectionExpanded',
  RiaCitatorCaseSelected = 'RiaCitatorCaseSelected',
  RiaCitatorRefreshClicked = 'RiaCitatorRefreshClicked',
  RiaSourceOpenInNewTabClicked = 'RiaSourceOpenInNewTabClicked',
  // AI Citator – Case treatment badge engagement
  CitatorBadgeImpression = 'CitatorBadgeImpression',
  CitatorBadgeClicked = 'CitatorBadgeClicked',
  CitatorPanelOpened = 'CitatorPanelOpened',
  CitatorTreatmentOutcomeClicked = 'CitatorTreatmentOutcomeClicked',
  CitatorReferenceGroupExpanded = 'CitatorReferenceGroupExpanded',
  // High-Fidelity Template Adherence – apply template at export (canvas artifacts)
  ArtifactTemplateSelected = 'ArtifactTemplateSelected',
  ArtifactTemplateApplyCompleted = 'ArtifactTemplateApplyCompleted',
  ArtifactTemplateApplyFailed = 'ArtifactTemplateApplyFailed',
  ArtifactExportCompleted = 'ArtifactExportCompleted',
  ArtifactGenerationCompleted = 'ArtifactGenerationCompleted',
  // Drafting entry points (PRD: Drafting Entry Points & Document-First) — re-entering an existing draft
  DraftEntryPointClicked = 'DraftEntryPointClicked',
  // Jurisdiction in Matters – adoption nudges on existing matters
  DefaultJurisdictionSettingsViewed = 'DefaultJurisdictionSettingsViewed',
  DefaultJurisdictionConfigureClicked = 'DefaultJurisdictionConfigureClicked',
  DefaultJurisdictionSaved = 'DefaultJurisdictionSaved',
  MatterJurisdictionTooltipLinkClicked = 'MatterJurisdictionTooltipLinkClicked',
  MatterJurisdictionCtaViewed = 'MatterJurisdictionCtaViewed',
  MatterJurisdictionCtaClicked = 'MatterJurisdictionCtaClicked',
}

export type AnalyticsEventProperties = {
  [AnalyticsEvent.SignUp]: object
  [AnalyticsEvent.PaymentMethodAdded]: {
    subscriptionInterval?: string
  }
  [AnalyticsEvent.NewChat]: {
    conversationMatterId?: string
  }
  [AnalyticsEvent.NewChatMessage]: {
    conversationMatterId?: string
    skill?: string
    skillSource?: SkillSource
    riaResearchEnabled?: boolean
  }
  [AnalyticsEvent.OpenExistingConversation]: object
  [AnalyticsEvent.OpenConversationSubmenu]: object
  [AnalyticsEvent.OpenConversationDeletionDialog]: object
  [AnalyticsEvent.ConversationDeleted]: object
  [AnalyticsEvent.DownloadConversationToDocx]: object
  [AnalyticsEvent.OpenConversationRetitleDialog]: object
  [AnalyticsEvent.ConversationRetitled]: object
  [AnalyticsEvent.CopyChatResponse]: object
  [AnalyticsEvent.DownloadChatResponseToDocx]: object
  [AnalyticsEvent.OpenChatDialog]: {
    option?: string
  }
  [AnalyticsEvent.FileAnalysisFileUpload]: object
  [AnalyticsEvent.AssistantFilePillPreviewOpened]: {
    conversationId?: string
  }
  [AnalyticsEvent.BooleanComposerSubmission]: object
  [AnalyticsEvent.CompareDocumentsSubmission]: object
  [AnalyticsEvent.SharePaxtonClicked]: object
  [AnalyticsEvent.DownloadChatSourceToDocx]: object
  [AnalyticsEvent.ReferralDialogOpened]: object
  [AnalyticsEvent.ReferralLinkCopied]: object
  [AnalyticsEvent.ReferralLinkCopiedFromInput]: object
  [AnalyticsEvent.ContactUsForEnterpriseClicked]: object
  [AnalyticsEvent.PaymentMethodAddedMonthly]: object
  [AnalyticsEvent.PaymentMethodAddedAnnual]: object
  [AnalyticsEvent.MatterCreated]: {
    matterJurisdictionId: string
    isStateMatterJurisdiction: boolean
    isFederalMatterJurisdiction: boolean
    isMatterJurisdictionUndecided: boolean
    matterStateJurisdiction: string
    matterFederalJurisdiction: string
  }
  [AnalyticsEvent.MatterUpdated]: {
    matterNameChanged: boolean
    matterClientChanged: boolean
    matterDescriptionChanged: boolean
    matterJurisdictionEntrySource: MatterJurisdictionEntrySource
    matterJurisdictionChanged: boolean
    matterJurisdictionAdopted: boolean
    matterJurisdictionId: string
    isStateMatterJurisdiction: boolean
    isFederalMatterJurisdiction: boolean
    isMatterJurisdictionUndecided: boolean
    matterStateJurisdiction: string
    matterFederalJurisdiction: string
  }
  [AnalyticsEvent.MatterDeleted]: object
  [AnalyticsEvent.MatterInviteSent]: {
    matterId: string
    organizationId: string
    recipientUserId: string
    selectedRecipientCount: number
    invitedCount: number
  }
  [AnalyticsEvent.MatterInviteSendFailed]: {
    matterId: string
    organizationId: string
    selectedRecipientCount: number
    errorType: string
    statusCode?: number
  }
  [AnalyticsEvent.MatterInviteAccepted]: {
    matterId: string
    organizationId: string
  }
  [AnalyticsEvent.MatterInviteAcceptFailed]: {
    matterId: string
    organizationId: string
    errorType: string
    statusCode?: number
  }
  [AnalyticsEvent.MatterInviteRejected]: {
    matterId: string
    organizationId: string
  }
  [AnalyticsEvent.MatterInviteRejectFailed]: {
    matterId: string
    organizationId: string
    errorType: string
    statusCode?: number
  }
  [AnalyticsEvent.MatterSelectionChanged]: {
    selectedMatterId: string
  }
  [AnalyticsEvent.OnboardingFormSubmitted]: {
    firstName: string
    lastName: string
    professionalRole: string
    areaOfPractice: string
    firmSize: string
    rankingFirstChoice: string
    rankingSecondChoice: string
    rankingThirdChoice: string
    paxtonReferrer: string
    socialNetwork: string
    professionalRoleOther: string
    areaOfPracticeOther: string
    rankingFirstChoiceOther: string
    rankingSecondChoiceOther: string
    rankingThirdChoiceOther: string
    paxtonReferrerOther: string
  }
  [AnalyticsEvent.SkillsModalOpened]: {
    skillSource: SkillSource
  }
  [AnalyticsEvent.SkillQuickActionClick]: {
    skillSource: SkillSource
    skill: string
  }
  [AnalyticsEvent.SkillsModalSelected]: {
    skill: string
  }
  [AnalyticsEvent.SkillPromptSubmitted]: {
    skill: string
    skillSource: SkillSource
  }
  [AnalyticsEvent.ArtifactEditorOpened]: {
    artifactId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    matterId: string
    artifactType?: string
  }
  [AnalyticsEvent.ArtifactEditorFormattingUsed]: {
    formatType: 'bold' | 'italic' | 'bulletList' | 'orderedList'
    artifactId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    matterId: string
  }
  [AnalyticsEvent.ArtifactEditorAutoSaveSuccess]: {
    artifactId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    matterId: string
    saveTimestamp: number
    contentLength: number
  }
  [AnalyticsEvent.ArtifactEditorSessionCompleted]: {
    sessionDuration: number
    finalContentLength: number
    saveCount: number
    formatActionsUsed: number
    artifactId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    matterId: string
  }
  [AnalyticsEvent.ArtifactEditorFirstEdit]: {
    artifactId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    matterId: string
    timeToFirstEdit: number
  }
  [AnalyticsEvent.ArtifactEditorTextSizeChanged]: {
    fromSize: TextSizeLabel
    toSize: TextSizeLabel
    artifactId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    matterId: string
  }
  [AnalyticsEvent.ArtifactEditorSaveError]: {
    errorType: string
    errorMessage: string
    artifactId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    matterId: string
    retryAttempt: number
  }
  [AnalyticsEvent.InlineEditSubmitted]: {
    inlineEditId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    selectionWordCount: number
    selectionFrom: number
    selectionTo: number
    isPreset: boolean
  }
  [AnalyticsEvent.InlineEditCompleted]: {
    inlineEditId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    inlineEditSuccess: boolean
    durationMs: number
    errorType?: string
  }
  [AnalyticsEvent.InlineEditResolved]: {
    inlineEditId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    inlineEditResolution: InlineEditResolvedAction
  }
  [AnalyticsEvent.SubscriptionNoticeBannerDismissed]: {
    subscriptionNoticeBannerVariant: SubscriptionNoticeBannerVariant
    subscriptionInterval: StripeSubscriptionInterval
  }
  [AnalyticsEvent.SubscriptionNoticeBannerCTAClicked]: {
    subscriptionNoticeBannerVariant: SubscriptionNoticeBannerVariant
    subscriptionInterval: StripeSubscriptionInterval
  }
  [AnalyticsEvent.StripePortalOpened]: object
  [AnalyticsEvent.AppUpdateRefreshClicked]: {
    appVersion: string
  }
  [AnalyticsEvent.ArtifactDownloadSuccess]: {
    artifactId: string
    artifactVersionId: string
    isMedChronArtifact: boolean
    medChronJobId?: string
  }
  [AnalyticsEvent.ArtifactDownloadFailed]: {
    artifactId: string
    artifactVersionId: string
    errorMessage: string
    isMedChronArtifact: boolean
    medChronJobId?: string
  }
  [AnalyticsEvent.ClipboardCopy]: {
    conversationMatterId?: string
    skill?: string
    skillSource?: SkillSource
  }
  [AnalyticsEvent.ClipboardPaste]: {
    conversationMatterId?: string
    skill?: string
    skillSource?: SkillSource
  }
  [AnalyticsEvent.ResponseThumbUp]: {
    skill?: string
    skillSource?: string
  }
  [AnalyticsEvent.ResponseThumbDown]: {
    skill?: string
    skillSource?: string
  }
  [AnalyticsEvent.ArtifactClipboardCopy]: {
    artifactId: string
    artifactVersionId: string
    sessionId: string
    matterId?: string
  }
  [AnalyticsEvent.ConversationMatterChanged]: {
    conversationId: string
    fromMatterId: string
    toMatterId: string
  }
  [AnalyticsEvent.MedSummaryFeatureOpened]: {
    feature: 'medical_billing_summaries'
  }
  [AnalyticsEvent.MedSummaryTabChanged]: {
    feature: 'medical_billing_summaries'
    fromTab: 'new' | 'list'
    toTab: 'new' | 'list'
  }
  [AnalyticsEvent.MedSummaryStarted]: {
    feature: 'medical_billing_summaries'
  }
  [AnalyticsEvent.MedSummaryNameEntered]: {
    feature: 'medical_billing_summaries'
    hasName: boolean
  }
  [AnalyticsEvent.MedSummaryFileUploadStarted]: {
    feature: 'medical_billing_summaries'
    fileName: string
    fileSize?: number
    uploadMethod: 'drag_drop' | 'browse' | 'drive'
  }
  [AnalyticsEvent.MedSummaryFileUploadCompleted]: {
    feature: 'medical_billing_summaries'
    fileName: string
    fileSize?: number
    uploadMethod: 'drag_drop' | 'browse' | 'drive'
  }
  [AnalyticsEvent.MedSummaryFileUploadFailed]: {
    feature: 'medical_billing_summaries'
    fileName: string
    fileSize?: number
    uploadMethod: 'drag_drop' | 'browse' | 'drive'
    errorMessage?: string
  }
  [AnalyticsEvent.MedSummaryDriveDialogOpened]: {
    feature: 'medical_billing_summaries'
  }
  [AnalyticsEvent.MedSummaryMatterFilesDialogOpened]: {
    feature: 'medical_billing_summaries'
  }
  [AnalyticsEvent.MedSummaryDriveFileSelected]: {
    feature: 'medical_billing_summaries'
    fileCount: number
  }
  [AnalyticsEvent.MedSummaryFileRemoved]: {
    feature: 'medical_billing_summaries'
    fileName: string
  }
  [AnalyticsEvent.MedSummaryGenerateClicked]: {
    feature: 'medical_billing_summaries'
    fileCount: number
    hasName: boolean
  }
  [AnalyticsEvent.MedSummaryJobCreated]: {
    feature: 'medical_billing_summaries'
    jobId: string // Empty string when using deferred job creation which doesn't return an ID
    fileCount: number
    hasName: boolean
  }
  [AnalyticsEvent.MedSummaryListViewed]: {
    feature: 'medical_billing_summaries'
  }
  [AnalyticsEvent.MedSummaryJobViewerOpened]: {
    medSummaryJobId: string
  }
  [AnalyticsEvent.MedSummaryDownloadStarted]: {
    feature: 'medical_billing_summaries'
    jobId: string
    jobStatus: string
  }
  [AnalyticsEvent.MedSummaryDownloadCompleted]: {
    feature: 'medical_billing_summaries'
    jobId: string
    jobStatus: string
  }
  [AnalyticsEvent.MedSummaryDownloadFailed]: {
    feature: 'medical_billing_summaries'
    jobId: string
    jobStatus: string
    errorMessage?: string
  }
  // Medical Billing Summary – Living asset (single matter billing summary)
  [AnalyticsEvent.MedSummaryAssetGenerateClicked]: {
    matterId: string
    medSummarySelectedFilesCount: number
  }
  [AnalyticsEvent.MedSummaryAssetUpdateClicked]: {
    medSummaryAssetId: string
    medSummarySelectedFilesCount: number
  }
  [AnalyticsEvent.MedSummaryAssetRetryFailedClicked]: {
    medSummaryAssetId: string
  }
  [AnalyticsEvent.MedSummaryAssetCompleteExcludingFailedClicked]: {
    medSummaryAssetId: string
  }
  [AnalyticsEvent.MedSummaryAssetFileRemoved]: {
    medSummaryAssetId: string
    medSummaryEntriesRemoved: number
  }
  [AnalyticsEvent.MedSummaryAssetDownloadClicked]: {
    medSummaryAssetId: string
  }
  [AnalyticsEvent.MedSummaryAssetReferenceViewerOpened]: {
    medSummaryAssetId: string
    medSummaryReferenceId: string
  }
  // Medical Chronology – Legacy Reports (download only)
  [AnalyticsEvent.MedChronDownloadClicked]: {
    medChronJobId: string
  }
  // Medical Chronology – Living asset (single matter chronology)
  [AnalyticsEvent.MedChronAssetChatClicked]: {
    medChronAssetId: string
    matterId: string
  }
  [AnalyticsEvent.MedChronAssetDownloadClicked]: {
    medChronAssetId: string
  }
  [AnalyticsEvent.MedChronAssetGenerateClicked]: {
    matterId: string
    medChronSelectedFilesCount: number
  }
  [AnalyticsEvent.MedChronAssetUpdateClicked]: {
    medChronAssetId: string
    medChronSelectedFilesCount: number
  }
  [AnalyticsEvent.MedChronAssetFileRemoved]: {
    medChronAssetId: string
    medChronEventsRemoved: number
  }
  [AnalyticsEvent.MedChronAssetReferenceViewerOpened]: {
    medChronAssetId: string
    medChronReferenceId: string
  }
  // Timeline (Single Matter) – Living asset
  [AnalyticsEvent.TimelineSourceOpenFailed]: {
    timelineSourceId: string
    timelineJobId?: string
  }
  [AnalyticsEvent.TimelineAssetReferenceViewerOpened]: {
    timelineAssetId: string
    timelineReferenceId: string
  }
  [AnalyticsEvent.TimelineAssetDownloadClicked]: {
    timelineAssetId: string
  }
  [AnalyticsEvent.TimelineAssetGenerateClicked]: {
    matterId: string
    timelineSelectedFilesCount: number
  }
  [AnalyticsEvent.TimelineAssetUpdateClicked]: {
    timelineAssetId: string
    timelineSelectedFilesCount: number
  }
  [AnalyticsEvent.TimelineAssetFileRemoved]: {
    timelineAssetId: string
    timelineEventsRemoved: number
  }
  [AnalyticsEvent.TimelineAssetChatClicked]: {
    timelineAssetId: string
    matterId: string
  }
  [AnalyticsEvent.SideBarBillingSummariesClicked]: object
  [AnalyticsEvent.ApplyTemplateTabOpened]: {
    conversationId: string
  }
  [AnalyticsEvent.ApplyTemplateTabClosed]: {
    conversationId: string
  }
  [AnalyticsEvent.TemplateSourceChosen]: {
    conversationId: string
    templateSourceType: TemplateSource
    isFirstTemplate: boolean
  }
  [AnalyticsEvent.TemplateDriveDialogOpened]: {
    conversationId: string
  }
  [AnalyticsEvent.TemplateSourceDialogDismissed]: {
    conversationId: string
  }
  [AnalyticsEvent.TemplateUploadStarted]: {
    conversationId: string
    filePath: string
  }
  [AnalyticsEvent.TemplateUploadCompleted]: {
    conversationId: string
    filePath: string
    durationMs?: number
  }
  [AnalyticsEvent.TemplateUploadFailed]: {
    conversationId: string
    filePath: string
    durationMs?: number
    errorType?: TemplateUploadErrorType
  }
  [AnalyticsEvent.TemplateResponseSucceeded]: {
    conversationId: string
  }
  [AnalyticsEvent.TemplateResponseFailed]: {
    conversationId: string
    errorType?: string
  }
  [AnalyticsEvent.TemplateResponseCanceled]: {
    conversationId: string
  }
  [AnalyticsEvent.TemplateInfluencedFeedback]: {
    conversationId: string
    positive: boolean
  }
  [AnalyticsEvent.WritingSampleNotifyMeClicked]: {
    conversationId: string
  }
  [AnalyticsEvent.OrganizationCreated]: {
    organizationId: string
    organizationCreatedInvitesCount: number
  }
  [AnalyticsEvent.OrganizationInviteAccepted]: object
  [AnalyticsEvent.OrganizationInviteSent]: {
    organizationId: string
    organizationInviteToEmail: string
    // InviteEmailStatus - the event fires on invite creation regardless of email outcome
    emailStatus: string
  }
  [AnalyticsEvent.OrganizationUserRemoved]: {
    organizationId: string
    organizationUserEmail: string
  }
  // Research in Assistant – Discovery & Intent
  [AnalyticsEvent.RiaToggleClicked]: {
    riaToggleState: RiaToggleState
    riaToggleName: RiaToggleName
  }
  [AnalyticsEvent.RiaSourcesButtonClicked]: object
  [AnalyticsEvent.RiaSourcesDialogOpened]: {
    riaDialogSource: RiaDialogSource
  }
  // Research in Assistant – Configuration & Submission
  [AnalyticsEvent.RiaSourcesTabChanged]: {
    riaFromTab: RiaTab
    riaToTab: RiaTab
  }
  [AnalyticsEvent.RiaSourcesClearAllClicked]: {
    riaClearedStatesCount: number
    riaClearedFederalCount: number
  }
  [AnalyticsEvent.RiaSourcesApplyClicked]: {
    riaSelectedStatesCount: number
    riaSelectedFederalCount: number
    riaAllFederalSourcesSelected: boolean
    riaIncludeFederalContent: boolean
    riaIncludeUnpublishedCaseLaw: boolean
    riaDateRange: string
    riaFederalAdded: number
    riaFederalRemoved: number
  }
  [AnalyticsEvent.RiaSelectAllFederalChecked]: {
    conversationId: string
  }
  [AnalyticsEvent.RiaSelectAllFederalUnchecked]: {
    conversationId: string
  }
  [AnalyticsEvent.RiaMessageWithResearchSubmitted]: {
    conversationId: string
    riaSelectedStatesCount: number
    riaSelectedFederalCount: number
    riaAllFederalSourcesSelected: boolean
    riaIncludeFederalContent: boolean
    riaIncludeUnpublishedCaseLaw: boolean
    riaDateRange: string
  }
  [AnalyticsEvent.RiaResponseReceived]: {
    conversationId: string
    riaMessageId: string
    riaReferencesCount: number
    riaCaselawReferencesCount: number
    riaLrrReferencesCount: number
    riaHasResearchResults: boolean
  }
  [AnalyticsEvent.RiaSourceViewerOpened]: {
    conversationId: string
    riaMessageId: string
    riaSourceCitationId: string
    riaSourceType: string
    riaSourceTriggerLocation: RiaSourceTriggerLocation
    riaSourceTitle?: string
    matterId?: string
  }
  [AnalyticsEvent.RiaSourceDownloadClicked]: {
    conversationId: string
    riaMessageId: string
    riaSourceCitationId: string
    riaSourceType: string
    riaSourceTriggerLocation: RiaSourceTriggerLocation
    matterId?: string
  }
  [AnalyticsEvent.RiaSourceViewerTabChanged]: {
    conversationId: string
    riaMessageId: string
    fromTab: RiaViewerTab
    toTab: RiaViewerTab
    matterId?: string
  }
  [AnalyticsEvent.RiaCaseAnalysisRequested]: {
    conversationId: string
    riaMessageId: string
    riaSourceCitationId: string
    riaSourceType: string
    riaSourceTriggerLocation: RiaSourceTriggerLocation
    riaSourceTitle?: string
    matterId?: string
  }
  [AnalyticsEvent.RiaCitatorSectionExpanded]: {
    conversationId: string
    riaMessageId: string
    riaCitatorSection: RiaCitatorSection
    riaCitatorSectionCount?: number
    matterId?: string
  }
  [AnalyticsEvent.RiaCitatorCaseSelected]: {
    conversationId: string
    riaMessageId: string
    riaCitatorSection: RiaCitatorSection
    riaCitatorCaseId: string
    riaCitatorCaseTitle?: string
    matterId?: string
  }
  [AnalyticsEvent.RiaCitatorRefreshClicked]: {
    conversationId: string
    riaMessageId: string
    riaCitatorSection?: RiaCitatorSection
    riaCitatorRefreshReason?: string
    matterId?: string
  }
  [AnalyticsEvent.RiaSourceOpenInNewTabClicked]: {
    conversationId: string
    riaMessageId: string
    riaSourceCitationId: string
    riaSourceType: string
    riaSourceTriggerLocation: RiaSourceTriggerLocation
    riaSourceTitle?: string
    matterId?: string
  }
  // AI Citator – Case treatment badge engagement. Case-agnostic by design.
  [AnalyticsEvent.CitatorBadgeImpression]: {
    citatorBadgeType: CitatorBadgeType
    citatorSurface: CitatorSurface
  }
  [AnalyticsEvent.CitatorBadgeClicked]: {
    citatorBadgeType: CitatorBadgeType
    citatorSurface: CitatorSurface
  }
  [AnalyticsEvent.CitatorPanelOpened]: {
    citatorPanelSource: CitatorPanelSource
    citatorSurface: CitatorSurface
  }
  [AnalyticsEvent.CitatorTreatmentOutcomeClicked]: {
    citatorTreatmentOutcome: CitatorTreatmentOutcome
    citatorSurface: CitatorSurface
  }
  [AnalyticsEvent.CitatorReferenceGroupExpanded]: {
    citatorSurface: CitatorSurface
    citatorContainsBadges: boolean
  }
  // High-Fidelity Template Adherence – properties are feature-prefixed (Amplitude property names share a global namespace)
  [AnalyticsEvent.ArtifactTemplateSelected]: {
    artifactVersionId: string
    conversationId: string
    matterId: string
    artifactTemplateSource: ArtifactTemplateSource
    isTemplateReplacement: boolean
  }
  [AnalyticsEvent.ArtifactTemplateApplyCompleted]: {
    artifactVersionId: string
    matterId: string
    artifactExportType: ArtifactExportType
    templateApplyDurationMs: number
  }
  [AnalyticsEvent.ArtifactTemplateApplyFailed]: {
    artifactVersionId: string
    matterId: string
    artifactExportType: ArtifactExportType
    templateApplyDurationMs: number
    templateApplyErrorType: ArtifactTemplateApplyErrorType
  }
  // Every successful export (download/save × with/without template). artifactId is the stable
  // draft-level key (joins to ArtifactGenerationCompleted); optional ids are unavailable in some paths.
  [AnalyticsEvent.ArtifactExportCompleted]: {
    artifactVersionId: string
    artifactId?: string
    artifactExportType: ArtifactExportType
    templateApplied: boolean
    conversationId?: string
    matterId?: string
  }
  [AnalyticsEvent.ArtifactGenerationCompleted]: {
    artifactId: string
    artifactVersionId: string
    conversationId: string
    matterId: string
  }
  [AnalyticsEvent.DraftEntryPointClicked]: {
    artifactId: string
    artifactVersionId: string
    conversationId: string
    matterId: string
    draftEntryPoint: DraftEntryPoint
  }
  // Jurisdiction in Matters – adoption nudges on existing matters
  [AnalyticsEvent.DefaultJurisdictionSettingsViewed]: object
  [AnalyticsEvent.DefaultJurisdictionConfigureClicked]: {
    hasExistingDefaultJurisdiction: boolean
  }
  [AnalyticsEvent.DefaultJurisdictionSaved]: {
    defaultJurisdictionId: string
    isStateDefaultJurisdiction: boolean
    isFederalDefaultJurisdiction: boolean
    isDefaultJurisdictionUndecided: boolean
    defaultStateJurisdiction: string
    defaultFederalJurisdiction: string
  }
  [AnalyticsEvent.MatterJurisdictionTooltipLinkClicked]: {
    matterId: string
    matterCreatedBeforeLaunch: boolean
  }
  [AnalyticsEvent.MatterJurisdictionCtaViewed]: {
    matterId: string
    matterCreatedBeforeLaunch: boolean
  }
  [AnalyticsEvent.MatterJurisdictionCtaClicked]: {
    matterId: string
    matterCreatedBeforeLaunch: boolean
  }
}
